<div class="allocation-detail-page">
  <header class="allocation-detail-header">
    <div class="allocation-detail-title-row">
      <a
        class="lm-icon-btn"
        [routerLink]="backCommands()"
        [attr.aria-label]="backLabel()"
        [attr.title]="backLabel()">
        <svg lucideArrowLeft class="lm-icon" aria-hidden="true"></svg>
      </a>
      <h1 class="text-title">{{ activityType()?.name ?? 'Allocation' }}</h1>
      <button
        type="button"
        class="lm-icon-btn"
        (click)="navigateToNext()"
        [disabled]="!nextActivityTypeId()"
        aria-label="Next activity"
        title="Next activity">
        <svg lucideChevronRight class="lm-icon" aria-hidden="true"></svg>
      </button>
    </div>
    <p *ngIf="activityType() as activity">
      {{ activity.category === 'PERSONAL' ? 'Personal' : 'Work' }}
    </p>
  </header>

  <section *ngIf="loading()" class="loading">Loading allocation detail…</section>

  <section *ngIf="loadError()" class="lm-notice lm-notice--danger">
    {{ loadError() }}
    <button type="button" class="lm-btn" (click)="refresh()">Retry</button>
  </section>

  <ng-container *ngIf="!loading() && !loadError()">
    <section class="allocation-state-card" [class.allocation-state-card--paused]="!!pausedBlock()">
      <app-timer-overlay
        *ngIf="isRunning() && runningBlock() as activeBlock; else notRunningState"
        [runningBlock]="activeBlock"
        [activityName]="activityType()?.name ?? activeBlock.activityTypeId"
        [slotProgress]="slotProgressLabel()"
        [remainingSlots]="remainingSlots()"
        [submitting]="blockMutationSubmitting()"
        [error]="blockMutationError()"
        (pause)="pauseRunningBlock()"
        (resume)="resumeRunningBlock()"
        (complete)="completeRunningBlock()"
        (autoAdvance)="onAutoAdvance()"
        (skip)="skipRunningBlock($event)"
        (hide)="goBack()"
        (refreshDayRequest)="refresh()">
      </app-timer-overlay>

      <ng-template #notRunningState>
        <div class="allocation-label">
          <div class="allocation-title">
            <span *ngIf="hasPriorityToday()" class="lm-badge lm-badge--priority">Priority</span>
            <span *ngIf="!hasPriorityToday() && hasFocusToday()" class="lm-badge lm-badge--focus">Focus today</span>
            <span *ngIf="isCompleted()" class="lm-badge lm-badge--done">Completed</span>
            <span *ngIf="pausedBlock()" class="lm-badge">Paused</span>
            <span *ngIf="isNotThisCycle()" class="lm-badge">Not this cycle</span>
          </div>
          <span *ngIf="isNotThisCycle()">{{ setAsideMetaLabel() }}</span>
          <span *ngIf="(allocation()?.slotCount ?? 0) > 0">
            Completed: {{ allocation()?.completedSlots }} / Planned: {{ allocation()?.slotCount }}
          </span>
          <span *ngIf="(allocation()?.slotCount ?? 0) === 0">No slots planned</span>
        </div>
        <div class="slot-adjustment-row">
          <label for="allocation-slot-count">Slots</label>
          <input
            id="allocation-slot-count"
            name="allocation-slot-count"
            type="number"
            min="0"
            [ngModel]="slotDraft()"
            (ngModelChange)="setSlotDraft($event)"
          />
          <button
            type="button"
            class="lm-btn lm-btn--primary"
            (click)="saveSlots()"
            [disabled]="slotSaving()">
            {{ slotSaving() ? 'Saving…' : 'Save slots' }}
          </button>
        </div>
        <p *ngIf="slotSaveError()" class="lm-error-text">{{ slotSaveError() }}</p>
        <p *ngIf="slotSaveSuccess()" class="loading loading--success">{{ slotSaveSuccess() }}</p>
        <div class="start-block-actions">
          <ng-container *ngIf="pausedBlock(); else startState">
            <button
              type="button"
              class="lm-btn lm-btn--go"
              (click)="resumePausedBlock()"
              [disabled]="!canResumePausedBlock() || blockMutationSubmitting()">
              Resume
            </button>
            <span class="start-duration">Paused</span>
          </ng-container>
          <ng-template #startState>
            <button
              type="button"
              class="lm-btn lm-btn--go"
              (click)="startBlock()"
              [disabled]="startSubmitting() || blockMutationSubmitting() || !activityType()">
              {{ startSubmitting() ? 'Starting…' : 'Start' }}
            </button>
            <span *ngIf="slotDurationMinutes() !== null" class="start-duration">
              {{ slotDurationMinutes() }} min
            </span>
            <span *ngIf="slotDurationMinutes() === null" class="start-duration">
              No duration set
            </span>
          </ng-template>
        </div>
        <p *ngIf="blockMutationError()" class="lm-error-text">{{ blockMutationError() }}</p>
      </ng-template>

      <div class="allocation-planning-actions">
        <button
          *ngFor="let targetGroup of moveTargets()"
          type="button"
          class="lm-btn"
          [attr.aria-label]="'Move allocation to ' + resolveScheduleGroupLabel(targetGroup)"
          (click)="moveToScheduleType(targetGroup.ordinal)"
          [disabled]="groupingSubmitting()">
          Move to {{ resolveScheduleGroupLabel(targetGroup) }}
        </button>
        <button
          *ngIf="isNotThisCycle()"
          type="button"
          class="lm-btn"
          (click)="clearNotThisCycle()"
          [disabled]="notThisCycleSubmitting()">
          <svg lucideUndo2 class="lm-icon lm-icon--sm" aria-hidden="true"></svg>
          Undo not this cycle
        </button>
        <button
          *ngIf="!isNotThisCycle() && !isRunning()"
          type="button"
          class="lm-btn"
          (click)="markNotThisCycle()"
          [disabled]="notThisCycleSubmitting()">
          <svg lucideBan class="lm-icon lm-icon--sm" aria-hidden="true"></svg>
          Not this cycle
        </button>
      </div>
      <p *ngIf="groupingError()" class="lm-error-text">{{ groupingError() }}</p>
      <p *ngIf="notThisCycleError()" class="lm-error-text">{{ notThisCycleError() }}</p>
    </section>

    <section class="tasks-section">
      <h3>Tasks</h3>
      <form class="create-task-form" (ngSubmit)="createTask()">
        <input
          type="text"
          name="newTaskTitle"
          maxlength="500"
          placeholder="Add task"
          [(ngModel)]="newTaskTitle"
        />
        <button type="submit" class="lm-btn lm-btn--primary" [disabled]="taskCreating() || !newTaskTitle.trim()">
          {{ taskCreating() ? 'Adding…' : 'Add task' }}
        </button>
        <label>
          Repeat
          <select
            name="newTaskRecurrenceType"
            [ngModel]="newTaskRecurrenceType()"
            (ngModelChange)="setNewTaskRecurrenceType($event)">
            <option value="NONE">None</option>
            <option value="DAILY">Daily</option>
            <option value="WEEKLY">Weekly</option>
            <option value="MONTHLY">Monthly</option>
          </select>
        </label>
        <div *ngIf="newTaskRecurrenceType() === 'WEEKLY'" class="weekday-picker" role="group" aria-label="Repeat on weekdays">
          <button type="button" class="weekday-picker-button" (click)="toggleWeeklyDay(1)" [class.active]="isWeeklyDaySelected(1)" [attr.aria-pressed]="isWeeklyDaySelected(1)">Mon</button>
          <button type="button" class="weekday-picker-button" (click)="toggleWeeklyDay(2)" [class.active]="isWeeklyDaySelected(2)" [attr.aria-pressed]="isWeeklyDaySelected(2)">Tue</button>
          <button type="button" class="weekday-picker-button" (click)="toggleWeeklyDay(3)" [class.active]="isWeeklyDaySelected(3)" [attr.aria-pressed]="isWeeklyDaySelected(3)">Wed</button>
          <button type="button" class="weekday-picker-button" (click)="toggleWeeklyDay(4)" [class.active]="isWeeklyDaySelected(4)" [attr.aria-pressed]="isWeeklyDaySelected(4)">Thu</button>
          <button type="button" class="weekday-picker-button" (click)="toggleWeeklyDay(5)" [class.active]="isWeeklyDaySelected(5)" [attr.aria-pressed]="isWeeklyDaySelected(5)">Fri</button>
          <button type="button" class="weekday-picker-button" (click)="toggleWeeklyDay(6)" [class.active]="isWeeklyDaySelected(6)" [attr.aria-pressed]="isWeeklyDaySelected(6)">Sat</button>
          <button type="button" class="weekday-picker-button" (click)="toggleWeeklyDay(7)" [class.active]="isWeeklyDaySelected(7)" [attr.aria-pressed]="isWeeklyDaySelected(7)">Sun</button>
        </div>
        <div *ngIf="newTaskRecurrenceType() === 'MONTHLY'" class="weekday-picker month-day-picker" role="group" aria-label="Repeat on days of the month">
          <button
            *ngFor="let day of monthDays"
            type="button"
            class="weekday-picker-button"
            (click)="toggleMonthlyDay(day)"
            [class.active]="isMonthlyDaySelected(day)"
            [attr.aria-pressed]="isMonthlyDaySelected(day)">{{ day }}</button>
        </div>
        <p *ngIf="newTaskRecurrenceType() === 'MONTHLY'" class="month-day-hint text-body-sm">The 29th to 31st fall on the last day in shorter months.</p>
      </form>

      <p *ngIf="taskError()" class="lm-error-text">{{ taskError() }}</p>

      <div class="task-group">
        <h4 class="task-group-header task-group-header--now text-overline">Now</h4>
        <p *ngIf="nowTarget() !== null" class="task-target">
          Target: {{ nowTarget() }} tasks
        </p>
        <p *ngIf="overNowTarget()" class="now-warning lm-notice lm-notice--warning">
          You are above the Now target. Keep only what you are actively doing now.
        </p>
        <ul
          class="task-list"
          cdkDropList
          cdkDropListOrientation="vertical"
          [cdkDropListDisabled]="taskReorderSubmitting()"
          (cdkDropListDropped)="onTaskGroupDrop(nowTasks(), $event)">
          <li
            *ngFor="let task of nowTasks()"
            class="task-row task-row--now"
            cdkDrag
            cdkDragLockAxis="y"
            [cdkDragDisabled]="taskReorderSubmitting() || isTaskBusy(task.id)"
            tabindex="0"
            [attr.aria-label]="'Reorder ' + task.title">
              <button
                type="button"
                class="star-btn star-active"
                (click)="unfocusTask(task)"
                [disabled]="isTaskBusy(task.id)"
                [attr.aria-label]="'Move ' + task.title + ' to later'">
                ★
              </button>
              <button type="button" class="task-open-link" (click)="openTask(task)">
                <span class="task-title">{{ task.title }}</span>
                <span *ngIf="getTaskItemCountLabel(task) as itemCountLabel" class="task-item-count-badge lm-badge">
                  {{ itemCountLabel }}
                </span>
              </button>
              <span class="lm-row__handle" cdkDragHandle aria-hidden="true">⋮⋮</span>
              <details class="task-move-menu">
                <summary [attr.aria-label]="'Move ' + task.title + ' to another activity type'">•••</summary>
                <div class="task-move-options">
                  <button
                    type="button"
                    class="lm-btn"
                    *ngFor="let activity of moveTaskTargets()"
                    (click)="moveTaskToActivityType(task, activity.id)"
                    [disabled]="isTaskBusy(task.id)">
                    Move to {{ activity.name }}
                  </button>
                </div>
              </details>
              <span *ngIf="isRecurringTask(task)" [attr.aria-label]="'Recurring task'" title="Recurring task">↻</span>
              <button
                type="button"
                class="pin-btn lm-btn"
                [class.pin-active]="isTaskPrioritizedToday(task)"
                [disabled]="isTaskBusy(task.id)"
                (click)="isTaskPrioritizedToday(task) ? unprioritizeTask(task) : prioritizeTask(task)"
                [attr.aria-label]="'Move ' + task.title + ' to next'">
                P
              </button>
              <input
                type="checkbox"
                [checked]="false"
                (change)="completeTask(task)"
                [disabled]="isTaskBusy(task.id)"
                [attr.aria-label]="'Complete ' + task.title"
              />
              <button
                *ngIf="!isRecurringTask(task)"
                type="button"
                class="lm-btn lm-btn--danger"
                (click)="deleteTask(task)"
                [disabled]="isTaskBusy(task.id)">
                Delete
              </button>
              <button
                *ngIf="isRecurringTask(task)"
                type="button"
                class="lm-btn lm-btn--danger"
                (click)="showDeleteChoices(task.id)"
                [disabled]="isTaskBusy(task.id) || seriesCancellingIds()[task.parentTaskId ?? task.id]">
                Delete
              </button>
            <div class="task-delete-choices" *ngIf="isRecurringTask(task) && hasDeleteChoices(task.id)">
              <button type="button" class="lm-btn lm-btn--danger" (click)="deleteTask(task)">Delete this occurrence</button>
              <button type="button" class="lm-btn lm-btn--danger" (click)="deleteTaskSeries(task)">Cancel series</button>
              <button type="button" class="lm-btn" (click)="hideDeleteChoices(task.id)">Cancel</button>
            </div>
          </li>
          <li *ngIf="nowTasks().length === 0" class="empty">No tasks in Now.</li>
        </ul>
      </div>

      <div class="task-group">
        <h4 class="task-group-header task-group-header--next text-overline">Next</h4>
        <ul
          class="task-list"
          cdkDropList
          cdkDropListOrientation="vertical"
          [cdkDropListDisabled]="taskReorderSubmitting()"
          (cdkDropListDropped)="onTaskGroupDrop(nextTasks(), $event)">
          <li
            *ngFor="let task of nextTasks()"
            class="task-row"
            cdkDrag
            cdkDragLockAxis="y"
            [cdkDragDisabled]="taskReorderSubmitting() || isTaskBusy(task.id)"
            tabindex="0"
            [attr.aria-label]="'Reorder ' + task.title">
              <button
                type="button"
                class="star-btn star-active"
                (click)="unfocusTask(task)"
                [disabled]="isTaskBusy(task.id)"
                [attr.aria-label]="'Move ' + task.title + ' to later'">
                ★
              </button>
              <button type="button" class="task-open-link" (click)="openTask(task)">
                <span class="task-title">{{ task.title }}</span>
                <span *ngIf="getTaskItemCountLabel(task) as itemCountLabel" class="task-item-count-badge lm-badge">
                  {{ itemCountLabel }}
                </span>
              </button>
              <span class="lm-row__handle" cdkDragHandle aria-hidden="true">⋮⋮</span>
              <details class="task-move-menu">
                <summary [attr.aria-label]="'Move ' + task.title + ' to another activity type'">•••</summary>
                <div class="task-move-options">
                  <button
                    type="button"
                    class="lm-btn"
                    *ngFor="let activity of moveTaskTargets()"
                    (click)="moveTaskToActivityType(task, activity.id)"
                    [disabled]="isTaskBusy(task.id)">
                    Move to {{ activity.name }}
                  </button>
                </div>
              </details>
              <span *ngIf="isRecurringTask(task)" [attr.aria-label]="'Recurring task'" title="Recurring task">↻</span>
              <button
                type="button"
                class="pin-btn lm-btn"
                [class.pin-active]="isTaskPrioritizedToday(task)"
                [disabled]="isTaskBusy(task.id)"
                (click)="isTaskPrioritizedToday(task) ? unprioritizeTask(task) : prioritizeTask(task)"
                [attr.aria-label]="'Move ' + task.title + ' to now'">
                P
              </button>
              <input
                type="checkbox"
                [checked]="false"
                (change)="completeTask(task)"
                [disabled]="isTaskBusy(task.id)"
                [attr.aria-label]="'Complete ' + task.title"
              />
              <button
                *ngIf="!isRecurringTask(task)"
                type="button"
                class="lm-btn lm-btn--danger"
                (click)="deleteTask(task)"
                [disabled]="isTaskBusy(task.id)">
                Delete
              </button>
              <button
                *ngIf="isRecurringTask(task)"
                type="button"
                class="lm-btn lm-btn--danger"
                (click)="showDeleteChoices(task.id)"
                [disabled]="isTaskBusy(task.id) || seriesCancellingIds()[task.parentTaskId ?? task.id]">
                Delete
              </button>
            <div class="task-delete-choices" *ngIf="isRecurringTask(task) && hasDeleteChoices(task.id)">
              <button type="button" class="lm-btn lm-btn--danger" (click)="deleteTask(task)">Delete this occurrence</button>
              <button type="button" class="lm-btn lm-btn--danger" (click)="deleteTaskSeries(task)">Cancel series</button>
              <button type="button" class="lm-btn" (click)="hideDeleteChoices(task.id)">Cancel</button>
            </div>
          </li>
          <li *ngIf="nextTasks().length === 0" class="empty">No tasks in Next.</li>
        </ul>
      </div>

      <div class="task-group">
        <h4 class="task-group-header task-group-header--later text-overline">Later</h4>
        <ul
          class="task-list"
          cdkDropList
          cdkDropListOrientation="vertical"
          [cdkDropListDisabled]="taskReorderSubmitting()"
          (cdkDropListDropped)="onTaskGroupDrop(laterTasks(), $event)">
          <li
            *ngFor="let task of laterTasks()"
            class="task-row"
            [class.task-row--prev-focused]="task.focusedDate !== null"
            cdkDrag
            cdkDragLockAxis="y"
            [cdkDragDisabled]="taskReorderSubmitting() || isTaskBusy(task.id)"
            tabindex="0"
            [attr.aria-label]="'Reorder ' + task.title">
              <button
                type="button"
                class="star-btn"
                (click)="focusTask(task)"
                [disabled]="isTaskBusy(task.id)"
                [attr.aria-label]="'Move ' + task.title + ' to next'">
                ☆
              </button>
              <button type="button" class="task-open-link" (click)="openTask(task)">
                <span class="task-title">{{ task.title }}</span>
                <span *ngIf="getTaskItemCountLabel(task) as itemCountLabel" class="task-item-count-badge lm-badge">
                  {{ itemCountLabel }}
                </span>
              </button>
              <span class="lm-row__handle" cdkDragHandle aria-hidden="true">⋮⋮</span>
              <details class="task-move-menu">
                <summary [attr.aria-label]="'Move ' + task.title + ' to another activity type'">•••</summary>
                <div class="task-move-options">
                  <button
                    type="button"
                    class="lm-btn"
                    *ngFor="let activity of moveTaskTargets()"
                    (click)="moveTaskToActivityType(task, activity.id)"
                    [disabled]="isTaskBusy(task.id)">
                    Move to {{ activity.name }}
                  </button>
                </div>
              </details>
              <span *ngIf="isRecurringTask(task)" [attr.aria-label]="'Recurring task'" title="Recurring task">↻</span>
              <input
                type="checkbox"
                [checked]="false"
                (change)="completeTask(task)"
                [disabled]="isTaskBusy(task.id)"
                [attr.aria-label]="'Complete ' + task.title"
              />
              <button
                *ngIf="!isRecurringTask(task)"
                type="button"
                class="lm-btn lm-btn--danger"
                (click)="deleteTask(task)"
                [disabled]="isTaskBusy(task.id)">
                Delete
              </button>
              <button
                *ngIf="isRecurringTask(task)"
                type="button"
                class="lm-btn lm-btn--danger"
                (click)="showDeleteChoices(task.id)"
                [disabled]="isTaskBusy(task.id) || seriesCancellingIds()[task.parentTaskId ?? task.id]">
                Delete
              </button>
            <div class="task-delete-choices" *ngIf="isRecurringTask(task) && hasDeleteChoices(task.id)">
              <button type="button" class="lm-btn lm-btn--danger" (click)="deleteTask(task)">Delete this occurrence</button>
              <button type="button" class="lm-btn lm-btn--danger" (click)="deleteTaskSeries(task)">Cancel series</button>
              <button type="button" class="lm-btn" (click)="hideDeleteChoices(task.id)">Cancel</button>
            </div>
          </li>
          <li *ngIf="laterTasks().length === 0" class="empty">No tasks in Later. Add one above.</li>
        </ul>
      </div>

      <div class="task-group">
        <button
          type="button"
          class="task-group-header task-group-toggle"
          [attr.aria-expanded]="completedExpanded()"
          [attr.aria-controls]="completedExpanded() ? completedTasksListId : null"
          (click)="toggleCompletedSection()">
          <span>Completed Tasks ({{ completedTasks().length }})</span>
          <svg *ngIf="completedExpanded(); else completedCollapsedChevron" lucideChevronUp class="lm-icon lm-icon--sm" aria-hidden="true"></svg>
          <ng-template #completedCollapsedChevron>
            <svg lucideChevronDown class="lm-icon lm-icon--sm" aria-hidden="true"></svg>
          </ng-template>
        </button>

        <ul *ngIf="completedExpanded()" [id]="completedTasksListId" class="task-list">
          <li *ngFor="let task of completedTasks()">
            <div class="task-row task-row--completed">
              <span class="task-title">{{ task.title }}</span>
              <input
                type="checkbox"
                checked
                (change)="reopenTask(task)"
                [disabled]="taskReopeningIds()[task.id]"
                [attr.aria-label]="'Reopen ' + task.title"
              />
            </div>
          </li>
          <li *ngIf="completedTasks().length === 0" class="empty">No completed tasks.</li>
        </ul>
      </div>
    </section>
  </ng-container>
</div>
